import React, { useState, useEffect, createContext, useContext } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, updateDoc, serverTimestamp, arrayUnion, writeBatch } from 'firebase/firestore';
import { auth, db } from '../index';
import { useDoc } from './useDoc';
import { computeSubscriptionState } from '../../data/subscriptionHelpers';
import { toGeoJSONPoint } from '../../data/geo';

const TenantContext = createContext(null);

// Codici di errore Firebase Auth che indicano una sessione lato client
// non più valida server-side (token "fantasma"). Tipico scenario di sviluppo:
// l'utente è stato cancellato dalla Firebase Console ma il browser ha ancora
// l'ID token in localStorage → ogni call a `accounts:lookup` ritorna 400 in
// console. La gestione corretta è fare logout silenzioso e lasciare che il
// guard di routing in App rimandi al /login.
const STALE_SESSION_CODES = new Set([
  'auth/user-not-found',
  'auth/user-token-expired',
  'auth/user-disabled',
  'auth/invalid-user-token',
  'auth/id-token-expired',
  'auth/id-token-revoked',
]);

export function TenantProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = caricamento in corso

  useEffect(() => {
    if (typeof window === 'undefined') return;
    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        return;
      }
      // Verifica che l'utente esista ancora server-side. `reload()` rifresca
      // il profilo dal server: se l'utente è stato eliminato/disabilitato
      // lancia uno dei STALE_SESSION_CODES → logout silenzioso, niente più
      // 400 "fantasma" sul `accounts:lookup`.
      try {
        await firebaseUser.reload();
        setUser(firebaseUser);
      } catch (err) {
        if (STALE_SESSION_CODES.has(err?.code)) {
          await signOut(auth).catch(() => {});
          setUser(null);
        } else {
          // Errori di rete o transient: mantieni la sessione locale, il
          // listener riproverà al prossimo refresh.
          setUser(firebaseUser);
        }
      }
    });
  }, []);

  const uid            = user?.uid || null;
  const userProfile    = useDoc(uid ? `users/${uid}` : null);
  // activeTenantId con fallback al primo tenant di appartenenza: se il campo
  // manca ma l'utente è membro di almeno un tenant (tenants[]), usa quello —
  // evita di trattare come "orfano" un account valido senza activeTenantId.
  const activeTenantId = userProfile?.activeTenantId || userProfile?.tenants?.[0] || null;

  // Documento tenant live (contiene `subscription`). Usato per il gating piano.
  const tenantDoc   = useDoc(activeTenantId ? `tenants/${activeTenantId}` : null);
  const subscription = tenantDoc?.subscription || null;

  const [membershipRole, setMembershipRole] = useState(null);

  useEffect(() => {
    if (!uid || !activeTenantId || !db) { setMembershipRole(null); return; }
    getDoc(doc(db, `tenants/${activeTenantId}/membership/${uid}`))
      .then((snap) => setMembershipRole(snap.exists() ? snap.data().role : null))
      .catch(() => setMembershipRole(null));
  }, [uid, activeTenantId]);

  async function switchTenant(newTenantId) {
    if (!uid || !db) return;
    await updateDoc(doc(db, `users/${uid}`), { activeTenantId: newTenantId });
    const snap = await getDoc(doc(db, `tenants/${newTenantId}/membership/${uid}`));
    setMembershipRole(snap.exists() ? snap.data().role : null);
  }

  // Crea tenant e imposta l'utente come owner (primo accesso / onboarding).
  // Signature: (nome, indirizzoSede, partitaIva, coords?).
  // - `coords`: opzionale [lat, lng] dal geocoding fatto a registrazione.
  //   Se passato come array valido di due numeri, viene salvato sul Deposito
  //   di default; altrimenti il Deposito è creato con coords:null (l'utente
  //   geocodificherà manualmente dal form di modifica).
  // Il settore merceologico è stato rimosso dal flusso di registrazione.
  async function createTenant(nome, indirizzoSede = '', partitaIva = '', coords = null) {
    // Usa auth.currentUser perché questa funzione può essere chiamata subito dopo
    // createUserWithEmailAndPassword, prima che React ri-renda il provider con il nuovo uid.
    const currentUser = auth.currentUser;
    const currentUid  = currentUser?.uid;
    if (!currentUid || !db) throw new Error('Non autenticato');

    const slugify = (s) => s.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-').replace(/^-|-$/g, '');
    const tenantId = slugify(nome);

    const tenantRef = doc(db, `tenants/${tenantId}`);
    const memberRef = doc(db, `tenants/${tenantId}/membership/${currentUid}`);
    const userRef   = doc(db, `users/${currentUid}`);

    const validCoords = Array.isArray(coords) && coords.length === 2
      && Number.isFinite(coords[0]) && Number.isFinite(coords[1]);

    const batch = writeBatch(db);
    batch.set(tenantRef, {
      nome,
      partitaIva: partitaIva || null,
      settore: null,                  // riservato per future feature (profilo tenant)
      createdAt: serverTimestamp(),
      createdBy: currentUid,
      // Dati di fatturazione: appartengono al TENANT (è l'azienda che paga, B2B),
      // non allo user. Pre-compilati dai dati di registrazione; completabili in
      // Profilo → Dati di fatturazione (owner-only). Vedi docs/DATABASE.md (Parte II).
      tax_info: {
        ragioneSociale: nome || '',
        partitaIva: partitaIva || '',
        codiceFiscale: '',
        codiceSDI: '',
        emailFatturazione: '',
        indirizzo: indirizzoSede || '',
        geo: validCoords ? toGeoJSONPoint(coords) : null,
      },
      // Abbonamento iniziale: piano Demo, periodo NON ancora avviato (parte alla
      // verifica email via ensureTrialStarted, che imposta start/end). La fase è
      // derivata da computeSubscriptionState. Vedi data/subscriptionHelpers.js.
      subscription: {
        plan: 'demo',
        billingCycle: 'demo',          // demo | monthly | annual (giorni: 0 | 30 | 365)
        subscriptionStart: null,
        subscriptionEnd: null,
        autoRenewal: true,
        // Moduli-area: anagrafiche core (sempre on); logistica/commerciale
        // opzionali. Durante il trial demo isModuleActive li tratta come attivi.
        modules: { anagrafiche: true, logistica: false, commerciale: false, payments: false, customerPortal: false },
        updatedAt: serverTimestamp(),
      },
    });
    batch.set(memberRef, { role: 'owner', joinedAt: serverTimestamp(), displayName: currentUser.displayName || currentUser.email, grantedBy: currentUid });
    batch.set(userRef, { email: currentUser.email, displayName: currentUser.displayName || '', tenants: arrayUnion(tenantId), activeTenantId: tenantId, updatedAt: serverTimestamp() }, { merge: true });
    // Deposito principale di default.
    // - docId Firestore = 'DEP-MAIN-001' (umano, leggibile, allineato al codice).
    //   I depositi creati successivamente dall'utente seguono DEP-001, DEP-002…
    //   (la parte MAIN distingue il deposito di registrazione da quelli successivi).
    // - type: 'Deposito' (fisso, è la sede principale dell'azienda TMS)
    // - coords: passate dal geocoding fatto a registrazione, oppure null se il
    //   geocoding ha fallito (l'utente potrà geocodificare manualmente dal form).
    //   Mai array vuoto: vedi guard isValidCoords() in LeafletMap.
    // - orariLavoro: default standard ufficio Lun-Ven 08:00-17:00 (uguali a
    //   quanto produce `parseWeeklySchedule(undefined)` in ClienteFormModal).
    //   Senza default il drawer di lettura mostrerebbe "—" finché l'utente non
    //   apre il form e salva (anche senza modifiche).
    // - adrCategorie: array vuoto esplicito (no ADR di default per un deposito).
    // - emailReferente/telefonoReferente: stringhe vuote esplicite.
    // - id: 'DEP-MAIN-001' (coincide con docId e codice — pattern coerente con
    //   tutte le location create successivamente dal form).
    const defaultOrariLavoro = [
      { giorno: 'Lun', oraInizio: '08:00', oraFine: '17:00' },
      { giorno: 'Mar', oraInizio: '08:00', oraFine: '17:00' },
      { giorno: 'Mer', oraInizio: '08:00', oraFine: '17:00' },
      { giorno: 'Gio', oraInizio: '08:00', oraFine: '17:00' },
      { giorno: 'Ven', oraInizio: '08:00', oraFine: '17:00' },
    ];
    const depotRef = doc(db, `tenants/${tenantId}/locations/DEP-MAIN-001`);
    batch.set(depotRef, {
      id: 'DEP-MAIN-001',
      codice: 'DEP-MAIN-001',
      type: 'Deposito',
      nome: `Sede principale — ${nome}`,
      address: indirizzoSede || '',
      geo: validCoords ? toGeoJSONPoint(coords) : null,
      orariLavoro: defaultOrariLavoro,
      adrCategorie: [],
      emailReferente: '',
      telefonoReferente: '',
      createdAt: serverTimestamp(),
    });

    try {
      await batch.commit();
    } catch (err) {
      if (err.code === 'permission-denied') {
        throw new Error(`Un'azienda con il nome "${nome}" esiste già. Scegli un nome diverso.`);
      }
      throw err;
    }
    return tenantId;
  }

  const role = membershipRole;

  const value = {
    user,
    uid,
    userProfile,
    activeTenantId,
    role,
    loading: user === undefined,
    isAuthenticated: !!user,
    emailVerified: user?.emailVerified ?? false,
    isOwner:      role === 'owner',
    isAdmin:      ['owner', 'admin'].includes(role),
    isDispatcher: ['owner', 'admin', 'dispatcher'].includes(role),
    isDriver:     role === 'driver',
    isProducer:   role === 'producer',
    isViewer:     !!role,
    subscription,
    subscriptionState: computeSubscriptionState(subscription, Date.now()),
    taxInfo: tenantDoc?.tax_info || null,   // dati di fatturazione del tenant
    switchTenant,
    createTenant,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used inside <TenantProvider>');
  return ctx;
}
